import {
  QueryClient,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { type Batcher, create as createBatcher, windowedFiniteBatchScheduler } from '@yornaath/batshit';

import { workspaceName } from '../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { useCloudPlatformDiscovery } from '../utils/hooks';
import type { DeploymentFixed, ListDeploymentsResponseFixed } from './api';
import type {
  DiscoverKubernetesConfigShortcutsResponse,
  DiscoverKubernetesDeploymentMaxReplicasResponse, // <-- Added
  GetMonitoringConfigResponse,
} from './gen/ai/h2o/mlops/deployer/v2/config_service_pb';
import { type DeploymentMetadata, deploymentMetadataName } from './gen/ai/h2o/mlops/deployer/v2/deployment_metadata_pb';
import type { BatchGetDeploymentsMetadataResponse } from './gen/ai/h2o/mlops/deployer/v2/deployment_metadata_service_pb';
import { type Deployment, deploymentName } from './gen/ai/h2o/mlops/deployer/v2/deployment_pb';
import type {
  DeleteDeploymentMonitoringRequest,
  DeleteDeploymentMonitoringResponse,
  DeleteDeploymentRequest,
  DeleteDeploymentResponse,
  ListDeploymentsRequest,
  PauseDeploymentRequest,
  PauseDeploymentResponse,
  ResumeDeploymentRequest,
  ResumeDeploymentResponse,
  RetryDeploymentRequest,
  RetryDeploymentResponse,
  UpdateDeploymentRequest,
  UpdateDeploymentResponse,
} from './gen/ai/h2o/mlops/deployer/v2/deployment_service_pb';
import { type DeploymentStatus, deploymentStatusName } from './gen/ai/h2o/mlops/deployer/v2/deployment_status_pb';
import type { BatchGetDeploymentStatusesResponse } from './gen/ai/h2o/mlops/deployer/v2/deployment_status_service_pb';
import { Endpoint, endpointName } from './gen/ai/h2o/mlops/deployer/v2/endpoint_pb';
import type {
  ListEndpointsRequest,
  ListEndpointsResponse,
  UpdateEndpointRequest,
} from './gen/ai/h2o/mlops/deployer/v2/endpoint_service_pb';
import {
  batchGetDeploymentMetadata,
  batchGetDeploymentStatuses,
  createEndpoint,
  deleteDeployment,
  deleteDeploymentMonitoring,
  deleteEndpoint,
  discoverKubernetesConfigShortcuts,
  discoverKubernetesDeploymentMaxReplicas,
  getDeployment,
  getMonitoringConfig,
  listDeployments,
  listEndpoints,
  pauseDeployment,
  resumeDeployment,
  retryDeployment,
  updateDeployment,
  updateEndpoint,
} from './mlops.deployer.functions';

export const cacheKeys = {
  configMonitoring: [`mlops`, `deployer`, `config`, `monitoring`] as const,
  configKubernetesShortcuts: [`mlops`, `deployer`, `config`, `kubernetesShortcuts`] as const,
  configKubernetesMaxReplicas: [`mlops`, `deployer`, `config`, `kubernetesMaxReplicas`] as const,
  deploymentsAll: (workspaceId: string, params?: Pick<ListDeploymentsRequest, `filter` | `orderBy`>) => {
    const baseKey: QueryKey = [`mlops`, `deployer`, workspaceId, `deployments`, `all`];
    if (!params) return baseKey;
    const { filter, orderBy } = params;
    return baseKey.concat({ filter: filter || ``, orderBy: orderBy || `` });
  },
  deploymentOne: (workspaceId: string, deploymentId: string) => {
    return [`mlops`, `deployer`, workspaceId, `deployments`, `one`, deploymentId];
  },
  deploymentMetadataOne: (workspaceId: string, deploymentId: string) => {
    return [...cacheKeys.deploymentOne(workspaceId, deploymentId), `metadata`];
  },
  deploymentStatusOne: (workspaceId: string, deploymentId: string) => {
    return [...cacheKeys.deploymentOne(workspaceId, deploymentId), `status`];
  },
  endpointsAll: (workspaceId: string, params?: Pick<ListEndpointsRequest, `filter` | `orderBy`>) => {
    const baseKey: QueryKey = [`mlops`, `deployer`, workspaceId, `endpoints`, `all`];
    if (!params) return baseKey;
    const { filter, orderBy } = params;
    return baseKey.concat({ filter: filter || ``, orderBy: orderBy || `` });
  },
};

// Config

export const useQueryKubernetesDeploymentMaxReplicas = <TData = DiscoverKubernetesDeploymentMaxReplicasResponse>(
  options?: UseQueryOptions<DiscoverKubernetesDeploymentMaxReplicasResponse, unknown, TData>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const baseOptions: UseQueryOptions<DiscoverKubernetesDeploymentMaxReplicasResponse, unknown, TData> = {
    queryKey: cacheKeys.configKubernetesMaxReplicas,
    queryFn: ({ signal }) => {
      return discoverKubernetesDeploymentMaxReplicas(mlopsApiUrl, {}, { signal });
    },
  };
  return useQuery({
    ...options,
    ...baseOptions,
  });
};

export const useQueryKubernetesConfigShortcuts = <TData = DiscoverKubernetesConfigShortcutsResponse>(
  options?: UseQueryOptions<DiscoverKubernetesConfigShortcutsResponse, unknown, TData>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const baseOptions: UseQueryOptions<DiscoverKubernetesConfigShortcutsResponse, unknown, TData> = {
    queryKey: cacheKeys.configKubernetesShortcuts,
    queryFn: ({ signal }) => {
      return discoverKubernetesConfigShortcuts(mlopsApiUrl, { signal });
    },
    staleTime: 60_000, // 1 minute
  };
  return useQuery({
    ...options,
    ...baseOptions,
  });
};

export const useQueryMonitoringConfig = <TData = GetMonitoringConfigResponse>(
  options?: UseQueryOptions<GetMonitoringConfigResponse, unknown, TData>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const baseOptions: UseQueryOptions<GetMonitoringConfigResponse, unknown, TData> = {
    queryKey: cacheKeys.configMonitoring,
    queryFn: ({ signal }) => {
      return getMonitoringConfig(mlopsApiUrl, { signal });
    },
    staleTime: 60_000, // 1 minute
  };
  return useQuery({
    ...options,
    ...baseOptions,
  });
};

// Deployments

export const useQueryDeployments = <TData = ListDeploymentsResponseFixed>(
  request: Omit<ListDeploymentsRequest, `parent`> & { workspaceId: string },
  options?: Omit<
    UseInfiniteQueryOptions<ListDeploymentsResponseFixed, unknown, TData>,
    `getNextPageParam` | `queryFn` | `queryKey`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const { workspaceId } = request;
  const baseOptions: UseInfiniteQueryOptions<ListDeploymentsResponseFixed, unknown, TData> = {
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    queryFn: ({ pageParam, signal }) => {
      const parent = workspaceName.compile({ workspace: workspaceId });
      return listDeployments(mlopsApiUrl, { ...request, parent, pageToken: pageParam }, { signal });
    },
    queryKey: cacheKeys.deploymentsAll(workspaceId, request),
  };
  return useInfiniteQuery({
    ...options,
    ...baseOptions,
  });
};

export const useMutationRetryDeployment = (
  options?: Omit<UseMutationOptions<RetryDeploymentResponse, unknown, RetryDeploymentRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: RetryDeploymentRequest) => {
      const response = await retryDeployment(mlopsApiUrl, request);
      const { workspace: workspaceId, deployment: deploymentId } = deploymentName.parse(request.name);
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentStatusOne(workspaceId, deploymentId),
      });
      return response;
    },
  });
};

export const useMutationDeleteDeploymentMonitoring = (
  options?: Omit<
    UseMutationOptions<DeleteDeploymentMonitoringResponse, unknown, DeleteDeploymentMonitoringRequest>,
    `mutationFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: DeleteDeploymentMonitoringRequest) => {
      const response = await deleteDeploymentMonitoring(mlopsApiUrl, request);
      const { workspace: workspaceId, deployment: deploymentId } = deploymentName.parse(request.name);
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentOne(workspaceId, deploymentId),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentStatusOne(workspaceId, deploymentId),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentMetadataOne(workspaceId, deploymentId),
        exact: false,
      });

      return response;
    },
  });
};

export const useQueryDeployment = <TData = DeploymentFixed>(
  { deploymentId, workspaceId }: { deploymentId: string; workspaceId: string },
  options?: Omit<UseQueryOptions<DeploymentFixed, unknown, TData>, `queryFn` | `queryKey`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const name = deploymentName.compile({ deployment: deploymentId, workspace: workspaceId });
  const baseOptions: UseQueryOptions<DeploymentFixed, unknown, TData> = {
    queryFn: async ({ signal }) => {
      const response = await getDeployment(mlopsApiUrl, { name }, { signal });
      return response.deployment;
    },
    queryKey: cacheKeys.deploymentOne(workspaceId, deploymentId),
  };
  return useQuery({
    ...options,
    ...baseOptions,
  });
};

export const useMutationBatchDeleteDeployments = (
  options?: Omit<UseMutationOptions<number, unknown, string[]>, `mutationFn`>
) => {
  const mutationDeleteDeployment = useMutationDeleteDeployment();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (deploymentNames: string[]) => {
      let successfullyDeletedCount = 0;

      for (const name of deploymentNames) {
        try {
          // Re-use the delete logic, but wait for each one to complete.
          await mutationDeleteDeployment.mutateAsync({ name });
          successfullyDeletedCount++;
        } catch (err) {
          // Log the error but continue with the next deployment.
          console.error(`Failed to delete deployment ${name} during batch operation:`, err);
        }
      }

      // Re-invalidate after the batch loop, although mutationDeleteDeployment
      // already handles invalidation for individual deployments. This ensures
      // the list refreshes once at the end of the batch operation.
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentsAll(`-`),
        exact: false,
      });

      return successfullyDeletedCount;
    },
  });
};

export const useMutationDeleteDeployment = (
  options?: Omit<UseMutationOptions<DeleteDeploymentResponse, unknown, DeleteDeploymentRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async ({ name }) => {
      const response = await deleteDeployment(mlopsApiUrl, { name });
      const { deployment: deploymentId, workspace: workspaceId } = deploymentName.parse(name);
      // Invalidate the list of deployments for the workspace
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentsAll(workspaceId),
        exact: false,
      });
      // Invalidate also the admin listing of deployments (`-` is a wildcard for all workspaces)
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentsAll(`-`),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.endpointsAll(workspaceId),
        exact: false,
      });
      // Remove everything related to the deleted deployment
      queryClient.removeQueries({
        queryKey: cacheKeys.deploymentOne(deploymentId, workspaceId),
        exact: true,
      });
      queryClient.removeQueries({
        queryKey: cacheKeys.deploymentMetadataOne(workspaceId, deploymentId),
        exact: true,
      });
      queryClient.removeQueries({
        queryKey: cacheKeys.deploymentStatusOne(workspaceId, deploymentId),
        exact: true,
      });
      return response;
    },
  });
};

/**
 * When updating a deployment, the fields marked as required must be present.
 */
export type DeploymentMinimumRequiredForUpdate = Deployment & Pick<Required<Deployment>, `name` | `etag`>;

export type PatchedUpdateDeploymentRequest = Omit<UpdateDeploymentRequest, `deployment`> & {
  deployment: DeploymentMinimumRequiredForUpdate;
};

export const useMutationUpdateDeployment = (
  options?: Omit<UseMutationOptions<UpdateDeploymentResponse, unknown, PatchedUpdateDeploymentRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (variables) => {
      const response = await updateDeployment(mlopsApiUrl, variables);
      const updatedDeployment = response.deployment!;
      const { workspace: workspaceId, deployment: deploymentId } = deploymentName.parse(updatedDeployment.name!);
      queryClient.setQueryData(cacheKeys.deploymentOne(workspaceId, deploymentId), updatedDeployment);
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentsAll(workspaceId),
        exact: false,
      });
      // Invalidate also the admin listing of deployments (`-` is a wildcard for all workspaces)
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentsAll(`-`),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.endpointsAll(workspaceId),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentMetadataOne(workspaceId, deploymentId),
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentStatusOne(workspaceId, deploymentId),
        exact: true,
      });
      return response;
    },
  });
};

export const useMutationPauseDeployment = (
  options?: Omit<UseMutationOptions<PauseDeploymentResponse, unknown, PauseDeploymentRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: PauseDeploymentRequest) => {
      const response = await pauseDeployment(mlopsApiUrl, request);
      const { workspace: workspaceId, deployment: deploymentId } = deploymentName.parse(request.name);

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: cacheKeys.deploymentsAll(workspaceId), exact: false });
      queryClient.invalidateQueries({ queryKey: cacheKeys.deploymentsAll(`-`), exact: false });
      queryClient.invalidateQueries({ queryKey: cacheKeys.endpointsAll(workspaceId), exact: false });

      // Invalidate specific deployment caches
      queryClient.invalidateQueries({ queryKey: cacheKeys.deploymentOne(workspaceId, deploymentId), exact: true });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentMetadataOne(workspaceId, deploymentId),
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentStatusOne(workspaceId, deploymentId),
        exact: true,
      });

      return response;
    },
  });
};

export const useMutationResumeDeployment = (
  options?: Omit<UseMutationOptions<ResumeDeploymentResponse, unknown, ResumeDeploymentRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: ResumeDeploymentRequest) => {
      const response = await resumeDeployment(mlopsApiUrl, request);
      const { workspace: workspaceId, deployment: deploymentId } = deploymentName.parse(request.name);

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: cacheKeys.deploymentsAll(workspaceId), exact: false });
      queryClient.invalidateQueries({ queryKey: cacheKeys.deploymentsAll(`-`), exact: false });
      queryClient.invalidateQueries({ queryKey: cacheKeys.endpointsAll(workspaceId), exact: false });

      // Invalidate specific deployment caches
      queryClient.invalidateQueries({ queryKey: cacheKeys.deploymentOne(workspaceId, deploymentId), exact: true });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentMetadataOne(workspaceId, deploymentId),
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.deploymentStatusOne(workspaceId, deploymentId),
        exact: true,
      });

      return response;
    },
  });
};

export const usePauseResumeDeployment = () => {
  const pauseDeploymentMutation = useMutationPauseDeployment();
  const resumeDeploymentMutation = useMutationResumeDeployment();

  const handlePauseDeployment = (deployment: DeploymentFixed) => {
    if (deployment.name) {
      pauseDeploymentMutation.mutate({ name: deployment.name });
    }
  };

  const handleResumeDeployment = (deployment: DeploymentFixed) => {
    if (deployment.name) {
      resumeDeploymentMutation.mutate({ name: deployment.name });
    }
  };

  return { handlePauseDeployment, handleResumeDeployment };
};

// Deployment Metadata

/**
 * A cache object for batchers, structured by MLOps API URL (should be constant, but there is no strong guarantee)
 * and workspace ID as the batch request cannot read across workspaces.
 * The top-most WeakMap is here just to isolate batchers in tests.
 */
type DeployerMetadataBatcher = Batcher<BatchGetDeploymentsMetadataResponse, string, DeploymentMetadata>;
const deploymentMetadataBatcherCache = new WeakMap<QueryClient, Map<string, Map<string, DeployerMetadataBatcher>>>();

const getDeploymentMetadataBatcher = (queryClient: QueryClient, mlopsApiUrl: string, workspaceId: string) => {
  if (!deploymentMetadataBatcherCache.has(queryClient)) {
    deploymentMetadataBatcherCache.set(queryClient, new Map());
  }
  const queryClientBatchers = deploymentMetadataBatcherCache.get(queryClient)!;
  if (!queryClientBatchers.has(mlopsApiUrl)) {
    queryClientBatchers.set(mlopsApiUrl, new Map());
  }
  const workspaceBatchers = queryClientBatchers.get(mlopsApiUrl)!;
  if (!workspaceBatchers.has(workspaceId)) {
    workspaceBatchers.set(
      workspaceId,
      createBatcher({
        fetcher: (names: string[]) =>
          batchGetDeploymentMetadata(mlopsApiUrl, {
            parent: workspaceName.compile({ workspace: workspaceId }),
            names,
          }),
        resolver: (batchResponse, name) => {
          const metadata = batchResponse.deploymentsMetadata?.find((m) => m.name === name);
          if (metadata) return metadata;
          const error = batchResponse.errors?.[name];
          if (error) return Promise.reject(error) as unknown as DeploymentMetadata;
          return Promise.reject(
            new Error(`Deployment metadata for ${name} not found`)
          ) as unknown as DeploymentMetadata;
        },
        scheduler: windowedFiniteBatchScheduler({
          windowMs: 10,
          maxBatchSize: 50,
        }),
      })
    );
  }
  return workspaceBatchers.get(workspaceId)!;
};

export const getDeploymentMetadataQueryOptions = <TData = DeploymentMetadata>({
  deploymentId,
  mlopsApiUrl,
  queryClient,
  workspaceId,
}: {
  deploymentId: string;
  mlopsApiUrl: string;
  queryClient: QueryClient;
  workspaceId: string;
}): UseQueryOptions<DeploymentMetadata, unknown, TData> => {
  return {
    queryKey: cacheKeys.deploymentMetadataOne(workspaceId, deploymentId),
    queryFn: () => {
      const name = deploymentMetadataName.compile({ workspace: workspaceId, deployment: deploymentId });
      return getDeploymentMetadataBatcher(queryClient, mlopsApiUrl, workspaceId).fetch(name);
    },
  };
};

export const useQueryDeploymentMetadata = <TData = DeploymentMetadata>(
  { deploymentId, workspaceId }: { deploymentId: string; workspaceId: string },
  options?: Omit<UseQueryOptions<DeploymentMetadata, unknown, TData>, `queryFn` | `queryKey`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  const baseOptions = getDeploymentMetadataQueryOptions({
    deploymentId,
    mlopsApiUrl,
    queryClient,
    workspaceId,
  }) as UseQueryOptions<DeploymentMetadata, unknown, TData>;
  return useQuery({
    ...options,
    ...baseOptions,
  });
};

// Deployment Status

/**
 * A cache object for batchers, structured by MLOps API URL (should be constant, but there is no strong guarantee)
 * and workspace ID as the batch request cannot read across workspaces.
 * The top-most WeakMap is here just to isolate batchers in tests.
 */
type DeploymentStatusesBatcher = Batcher<BatchGetDeploymentStatusesResponse, string, DeploymentStatus>;
const deploymentStatusesBatcherCache = new WeakMap<QueryClient, Map<string, Map<string, DeploymentStatusesBatcher>>>();

const getDeploymentStatusBatcher = (queryClient: QueryClient, mlopsApiUrl: string, workspaceId: string) => {
  if (!deploymentStatusesBatcherCache.has(queryClient)) {
    deploymentStatusesBatcherCache.set(queryClient, new Map());
  }
  const queryClientBatchers = deploymentStatusesBatcherCache.get(queryClient)!;
  if (!queryClientBatchers.has(mlopsApiUrl)) {
    queryClientBatchers.set(mlopsApiUrl, new Map());
  }
  const workspaceBatchers = queryClientBatchers.get(mlopsApiUrl)!;
  if (!workspaceBatchers.has(workspaceId)) {
    workspaceBatchers.set(
      workspaceId,
      createBatcher({
        fetcher: (names: string[]) =>
          batchGetDeploymentStatuses(mlopsApiUrl, { parent: workspaceName.compile({ workspace: workspaceId }), names }),
        resolver: (batchResponse, name) => {
          const status = batchResponse.deploymentStatuses?.find((s) => s.name === name);
          if (status) return status;
          const error = batchResponse.errors?.[name];
          if (error) return Promise.reject(error) as unknown as DeploymentStatus;
          return Promise.reject(new Error(`Deployment status for ${name} not found`)) as unknown as DeploymentStatus;
        },
        scheduler: windowedFiniteBatchScheduler({
          windowMs: 10,
          maxBatchSize: 50,
        }),
      })
    );
  }
  return workspaceBatchers.get(workspaceId)!;
};

export const getDeploymentStatusQueryOptions = <TData = DeploymentStatus>({
  deploymentId,
  mlopsApiUrl,
  queryClient,
  workspaceId,
}: {
  deploymentId: string;
  mlopsApiUrl: string;
  queryClient: QueryClient;
  workspaceId: string;
}): UseQueryOptions<DeploymentStatus, unknown, TData> => {
  return {
    queryKey: cacheKeys.deploymentStatusOne(workspaceId, deploymentId),
    queryFn: () => {
      const name = deploymentStatusName.compile({ workspace: workspaceId, deployment: deploymentId });
      return getDeploymentStatusBatcher(queryClient, mlopsApiUrl, workspaceId).fetch(name);
    },
  };
};

export const useQueryDeploymentStatus = <TData = DeploymentStatus>(
  { deploymentId, workspaceId }: { deploymentId: string; workspaceId: string },
  options?: Omit<UseQueryOptions<DeploymentStatus, unknown, TData>, `queryFn` | `queryKey`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  const baseOptions = getDeploymentStatusQueryOptions({
    deploymentId,
    mlopsApiUrl,
    queryClient,
    workspaceId,
  }) as UseQueryOptions<DeploymentStatus, unknown, TData>;
  return useQuery({
    ...options,
    ...baseOptions,
  });
};

// Endpoints

export type EndpointMinimumRequiredForCreate = Endpoint & Pick<Required<Endpoint>, `path` | `target`>;

export const useMutationCreateEndpoint = (
  options?: UseMutationOptions<Endpoint, unknown, EndpointMinimumRequiredForCreate>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (endpoint) => {
      const { workspace: workspaceId } = deploymentName.parse(endpoint.target);
      const workspaceFQN = workspaceName.compile({ workspace: workspaceId });
      const response = await createEndpoint(mlopsApiUrl, {
        endpoint,
        parent: workspaceFQN,
      });
      const createdEndpoint = response.endpoint!;
      queryClient.invalidateQueries({
        queryKey: cacheKeys.endpointsAll(workspaceId),
        exact: false,
      });
      return createdEndpoint;
    },
  });
};

export const useMutationDeleteEndpoint = (options?: Omit<UseMutationOptions<void, unknown, string>, `mutationFn`>) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (name: string) => {
      const { workspace: workspaceId } = endpointName.parse(name);
      await deleteEndpoint(mlopsApiUrl, { name });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.endpointsAll(workspaceId),
        exact: false,
      });
    },
  });
};

export const useQueryEndpoints = <TData = ListEndpointsResponse>(
  request: Omit<ListEndpointsRequest, `pageToken` | `parent`> & { workspaceId: string },
  options?: Omit<
    UseInfiniteQueryOptions<ListEndpointsResponse, unknown, TData>,
    `getNextPageParam` | `queryFn` | `queryKey`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const { workspaceId } = request;
  const baseOptions: UseInfiniteQueryOptions<ListEndpointsResponse, unknown, TData> = {
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    queryFn: ({ pageParam, signal }) => {
      const parent = workspaceName.compile({ workspace: workspaceId });
      return listEndpoints(mlopsApiUrl, { ...request, parent, pageToken: pageParam }, { signal });
    },
    queryKey: cacheKeys.endpointsAll(workspaceId, request),
  };
  return useInfiniteQuery({
    ...options,
    ...baseOptions,
  });
};

export type EndpointMinimumRequiredForUpdate = Endpoint & Pick<Required<Endpoint>, `name`>;
type UseMutationOptionsEndpointUpdate = UseMutationOptions<
  Endpoint,
  unknown,
  UpdateEndpointRequest & { endpoint: EndpointMinimumRequiredForUpdate }
>;
export const useMutationUpdateEndpoint = (options?: Omit<UseMutationOptionsEndpointUpdate, `mutationFn`>) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request) => {
      const { workspace: workspaceId } = endpointName.parse(request.endpoint.name);
      const response = await updateEndpoint(mlopsApiUrl, request);
      const updatedEndpoint = response.endpoint!;
      queryClient.invalidateQueries({
        queryKey: cacheKeys.endpointsAll(workspaceId),
        exact: false,
      });
      return updatedEndpoint;
    },
  });
};
